import type { ComponentType } from "react";

import { BlogIndexPage } from "./blog/blog-index/BlogIndexPage";
import { BlogPage } from "./blog/BlogPage";
import { GettingStartedPage } from "./blog/getting-started-with-companion/GettingStartedPage";
import { HomePage } from "./HomePage";
import { PRICING_FAQS } from "./pricing/PricingFAQ";
import { PricingPage } from "./pricing/PricingPage";
import { HOMEPAGE_FAQS } from "./sections/FAQ";
import { MedicalMathPage } from "./use-cases/dose-calculations-within-companion/MedicalMathPage";
import { UnfamiliarCasePage } from "./use-cases/how-companion-supports-an-unfamiliar-case-from-intake-to-referral/UnfamiliarCasePage";
import { UseCasesIndexPage } from "./use-cases/use-cases-index/UseCasesIndexPage";

export type FaqEntry = { question: string; answer: string };

export type RouteEntry = {
  path: string;
  Component: ComponentType;
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogType: "website" | "article";
  twitterCard: "summary" | "summary_large_image";
  canonical: string;
  faqEntries?: FaqEntry[];
};

export const routes: RouteEntry[] = [
  {
    path: "/",
    Component: HomePage,
    title: "Companion by Scribenote",
    description:
      "The AI Companion for vets who'd rather be with patients. Ask anything, about any patient, anytime. Get started with 500 free Treats.",
    ogTitle: "Companion by Scribenote",
    ogDescription:
      "The AI Companion for vets who'd rather be with patients. Ask anything, about any patient, anytime. Get started with 500 free Treats.",
    ogType: "website",
    twitterCard: "summary",
    canonical: "https://www.askcompanion.com/",
    faqEntries: HOMEPAGE_FAQS,
  },
  {
    path: "/pricing",
    Component: PricingPage,
    title: "Pricing — Companion by Scribenote",
    description:
      "Companion pricing — pay for the work, not the seat. Treat Pack, Munchkin, and Chonk plans. 500 free Treats to start.",
    ogTitle: "Pricing — Companion by Scribenote",
    ogDescription:
      "Pay for the work, not the seat. Treat Pack, Munchkin, and Chonk plans. 500 free Treats to start.",
    ogType: "website",
    twitterCard: "summary",
    canonical: "https://www.askcompanion.com/pricing/",
    faqEntries: PRICING_FAQS,
  },
  {
    path: "/blog",
    Component: BlogIndexPage,
    title: "Blog — Companion by Scribenote",
    description:
      "Notes from the team building Companion — essays, product updates, and engineering deep-dives from Scribenote.",
    ogTitle: "Blog — Companion by Scribenote",
    ogDescription:
      "Notes from the team building Companion — essays, product updates, and engineering deep-dives from Scribenote.",
    ogType: "website",
    twitterCard: "summary",
    canonical: "https://www.askcompanion.com/blog/",
  },
  {
    path: "/blog/life-is-better-with-companions",
    Component: BlogPage,
    title: "Life is better with Companions — Scribenote",
    description:
      "The team that brought AI scribing to veterinary medicine is launching an advanced new veterinary intelligence. A digital brain for your clinic.",
    ogTitle: "Life is better with Companions — Scribenote",
    ogDescription:
      "Why we believe a digital brain for the veterinary clinic is inevitable, and what we've been building.",
    ogType: "article",
    twitterCard: "summary_large_image",
    canonical:
      "https://www.askcompanion.com/blog/life-is-better-with-companions/",
  },
  {
    path: "/blog/getting-started-with-companion",
    Component: GettingStartedPage,
    title: "Getting Started with Companion — Scribenote",
    description:
      "Companion is your AI assistant inside Scribenote. Learn how to use it before, during, and after appointments — and get the most out of your first week.",
    ogTitle: "Getting Started with Companion — Scribenote",
    ogDescription:
      "Your AI assistant inside Scribenote. Documentation, research, client communication, and more — so you can stay focused on the patient in front of you.",
    ogType: "article",
    twitterCard: "summary_large_image",
    canonical:
      "https://www.askcompanion.com/blog/getting-started-with-companion/",
  },
  {
    path: "/use-cases",
    Component: UseCasesIndexPage,
    title: "Use cases — Companion by Scribenote",
    description:
      "See how vets use Companion in real workflows — differentials, client comms, handouts, referrals, documentation, and more.",
    ogTitle: "Use cases — Companion by Scribenote",
    ogDescription:
      "See how vets use Companion in real workflows — differentials, client comms, handouts, referrals, documentation, and more.",
    ogType: "website",
    twitterCard: "summary",
    canonical: "https://www.askcompanion.com/use-cases/",
  },
  {
    path: "/use-cases/how-companion-supports-an-unfamiliar-case-from-intake-to-referral",
    Component: UnfamiliarCasePage,
    title:
      "How Companion Supports an Unfamiliar Case from Intake to Referral — Scribenote",
    description:
      "Differentials, a communication plan, a client handout, and a specialist referral email — all from one conversation with Companion.",
    ogTitle:
      "How Companion Supports an Unfamiliar Case from Intake to Referral — Scribenote",
    ogDescription:
      "Differentials, a communication plan, a client handout, and a specialist referral email — all from one conversation with Companion.",
    ogType: "article",
    twitterCard: "summary_large_image",
    canonical:
      "https://www.askcompanion.com/use-cases/how-companion-supports-an-unfamiliar-case-from-intake-to-referral/",
  },
  {
    path: "/use-cases/dose-calculations-within-companion",
    Component: MedicalMathPage,
    title:
      "Dose Calculations, Fluid Math, Treatment Plans — All Within Companion",
    description:
      "Watch how one vet used Companion to build a full V/D treatment plan and calculate a gabapentin course, all from one conversation with Companion.",
    ogTitle:
      "Dose Calculations, Fluid Math, Treatment Plans — All Within Companion",
    ogDescription:
      "Watch how one vet used Companion to build a full V/D treatment plan and calculate a gabapentin course, all from one conversation with Companion.",
    ogType: "article",
    twitterCard: "summary_large_image",
    canonical:
      "https://www.askcompanion.com/use-cases/dose-calculations-within-companion/",
  },
];
